import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  Session, SessionStatus, SearchParams, Flight, Passenger,
  PaymentInfo, AgentOverride, BOOKING_STEPS,
} from '@airline-demo/types';

// ── Slice Types ───────────────────────────────────────────────────────────────

export interface AuthSlice {
  isAuthenticated: boolean;
  userEmail: string | null;
  userName: string | null;
  cognitoSub: string | null;
  photoUrl: string | null;
  loyaltyTier: string | null;
  milesBalance: number | null;
  frequentFlyerNumber: string | null;
  // Travel preferences
  prefSeatLocation: string | null;
  prefCabinClass:   string | null;
  prefMealType:     string | null;
  prefHomeAirport:  string | null;
  prefTravelType:   string | null;
  prefLanguage:     string | null;
  // Personal details
  birthDate:        string | null;
  passportNumber:   string | null;
  addressLine1:     string | null;
  addressCity:      string | null;
  addressState:     string | null;
  addressZip:       string | null;
  addressCountry:   string | null;
  // Onboarding
  wizardCompleted:  boolean | null;
  isAdmin:          boolean;
}

export interface SessionSlice {
  sessionId: string | null;
  userId: string | null;
  phoneNumber: string | null;
  status: SessionStatus;
  currentStep: number;
  lastUpdatedAt: string | null;
  isSessionLoading: boolean;
  agentNotes: string | null;
  agentOverride: AgentOverride | null;
}

export interface SearchSlice {
  searchParams: SearchParams | null;
}

export interface BookingSlice {
  selectedFlight: Flight | null;
  selectedSeats: string[];
  passengers: Passenger[];
  payment: PaymentInfo | null;
  confirmationCode: string | null;
}

export interface UiSlice {
  isSearching: boolean;
  isSyncing: boolean;
  error: string | null;
  flightResults: Flight[];
}

// ── Combined Store ────────────────────────────────────────────────────────────

export interface AirlineStore extends AuthSlice, SessionSlice, SearchSlice, BookingSlice, UiSlice {
  // Auth actions
  setAuth: (auth: Partial<AuthSlice>) => void;
  clearAuth: () => void;

  // Session actions
  setSession: (session: Partial<Session>) => void;
  setSessionLoading: (loading: boolean) => void;
  clearAgentOverride: () => void;

  // Search actions
  setSearchParams: (params: SearchParams) => void;

  // Booking actions
  selectFlight: (flight: Flight) => void;
  setSelectedSeats: (seats: string[]) => void;
  toggleSeat: (seatId: string) => void;
  setPassengers: (passengers: Passenger[]) => void;
  updatePassenger: (index: number, data: Partial<Passenger>) => void;
  setPayment: (payment: PaymentInfo) => void;
  setConfirmationCode: (code: string) => void;
  setCurrentStep: (step: number) => void;

  // UI actions
  setFlightResults: (flights: Flight[]) => void;
  setSearching: (loading: boolean) => void;
  setSyncing: (syncing: boolean) => void;
  setError: (error: string | null) => void;

  // Hydrate the entire store from a persisted Session
  hydrateFromSession: (session: Session) => void;

  // Reset to initial state (new session)
  reset: () => void;
}

type StoreState = Omit<AirlineStore,
  'setAuth' | 'clearAuth' |
  'setSession' | 'setSessionLoading' | 'clearAgentOverride' |
  'setSearchParams' | 'selectFlight' | 'setSelectedSeats' | 'toggleSeat' |
  'setPassengers' | 'updatePassenger' | 'setPayment' | 'setConfirmationCode' | 'setCurrentStep' |
  'setFlightResults' | 'setSearching' | 'setSyncing' | 'setError' |
  'hydrateFromSession' | 'reset'
>;

const initialState: StoreState = {
  // Auth
  isAuthenticated: false,
  userEmail: null,
  userName: null,
  cognitoSub: null,
  photoUrl: null,
  loyaltyTier: null,
  milesBalance: null,
  frequentFlyerNumber: null,
  prefSeatLocation: null,
  prefCabinClass:   null,
  prefMealType:     null,
  prefHomeAirport:  null,
  prefTravelType:   null,
  prefLanguage:     null,
  birthDate:        null,
  passportNumber:   null,
  addressLine1:     null,
  addressCity:      null,
  addressState:     null,
  addressZip:       null,
  addressCountry:   null,
  wizardCompleted:  null,
  isAdmin:          false,
  // Session
  sessionId: null,
  userId: null,
  phoneNumber: null,
  status: 'SEARCHING',
  currentStep: BOOKING_STEPS.SEARCH,
  lastUpdatedAt: null,
  isSessionLoading: true,
  agentNotes: null,
  agentOverride: null,
  // Search
  searchParams: null,
  // Booking
  selectedFlight: null,
  selectedSeats: [],
  passengers: [],
  payment: null,
  confirmationCode: null,
  // UI
  isSearching: false,
  isSyncing: false,
  error: null,
  flightResults: [],
};

export const useStore = create<AirlineStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // ── Auth ──────────────────────────────────────────────────────────────────
    setAuth: (auth) => set((s) => ({ ...s, ...auth })),
    clearAuth: () => set({
      isAuthenticated: false,
      userEmail: null,
      userName: null,
      cognitoSub: null,
      photoUrl: null,
      loyaltyTier: null,
      milesBalance: null,
      frequentFlyerNumber: null,
      prefSeatLocation: null,
      prefCabinClass:   null,
      prefMealType:     null,
      prefHomeAirport:  null,
      prefTravelType:   null,
      prefLanguage:     null,
      birthDate:        null,
      passportNumber:   null,
      addressLine1:     null,
      addressCity:      null,
      addressState:     null,
      addressZip:       null,
      addressCountry:   null,
      wizardCompleted:  null,
      isAdmin:          false,
    }),

    // ── Session ───────────────────────────────────────────────────────────────
    setSession: (session) => set((s) => ({ ...s, ...session })),
    setSessionLoading: (loading) => set({ isSessionLoading: loading }),
    clearAgentOverride: () => set({ agentOverride: null }),

    // ── Search ────────────────────────────────────────────────────────────────
    setSearchParams: (params) => set({ searchParams: params }),

    // ── Booking ───────────────────────────────────────────────────────────────
    selectFlight: (flight) => set({ selectedFlight: flight }),
    setSelectedSeats: (seats) => set({ selectedSeats: seats }),
    toggleSeat: (seatId) => {
      const current = get().selectedSeats;
      const next = current.includes(seatId)
        ? current.filter((s) => s !== seatId)
        : [...current, seatId];
      set({ selectedSeats: next });
    },
    setPassengers: (passengers) => set({ passengers }),
    updatePassenger: (index, data) => {
      const passengers = [...get().passengers];
      passengers[index] = { ...passengers[index], ...data };
      set({ passengers });
    },
    setPayment: (payment) => set({ payment }),
    setConfirmationCode: (code) => set({ confirmationCode: code }),
    setCurrentStep: (step) => set({ currentStep: step }),

    // ── UI ────────────────────────────────────────────────────────────────────
    setFlightResults: (flights) => set({ flightResults: flights }),
    setSearching: (loading) => set({ isSearching: loading }),
    setSyncing: (syncing) => set({ isSyncing: syncing }),
    setError: (error) => set({ error }),

    // ── Hydration ─────────────────────────────────────────────────────────────
    hydrateFromSession: (session: Session) => {
      const bs = session.bookingState ?? {};
      set({
        sessionId: session.sessionId,
        userId: session.userId ?? null,
        phoneNumber: session.phoneNumber ?? null,
        status: session.status,
        currentStep: session.currentStep,
        lastUpdatedAt: session.lastUpdatedAt,
        agentNotes: session.agentNotes ?? null,
        agentOverride: session.agentOverride ?? null,
        searchParams: bs.searchParams ?? null,
        selectedFlight: bs.selectedFlight ?? null,
        selectedSeats: bs.selectedSeats ?? [],
        passengers: bs.passengers ?? [],
        payment: bs.payment ?? null,
        confirmationCode: bs.confirmationCode ?? null,
        isSessionLoading: false,
      });
    },

    reset: () => set({ ...initialState, isSessionLoading: false }),
  })),
);
